

import React, { useState, useEffect, useRef } from 'react';
import { auth, database } from '../firebase';
import { ref, onValue, push, set, get, update } from 'firebase/database';
import CustomerForm from './CustomerForms';

const SalesForm = ({ onClose = () => {}, onSubmit = () => {}, saleToEdit = null }) => {
  const [formData, setFormData] = useState({
    billNo: 1,
    date: saleToEdit?.date || new Date().toLocaleDateString('en-GB').split('/').join('-'),
    terms: saleToEdit?.terms || 'CASH',
    salesMan: saleToEdit?.salesMan || 'Loading...',
    customer: saleToEdit?.customer || '',
    phone: saleToEdit?.phone || '',
    taxPercent: "",
    items: saleToEdit?.items || [
      {
        sNo: 1,
        itemDescription: '',
        unit: '',
        qty: '',
        discPercent: '',
        unitRate: '',
        tax: '',
        netValue: '',
      },
    ],
    totals: saleToEdit?.totals || {
      grossAmt: 0,
      discount: 0,
      billDiscount: 0,
      totalTax: 0,
      netAmount: 0,
    },
    topInput: {
      itemDescription: '',
      qty: '',
      disc: '',
      unitRate: '',
      tax: '',
    },
    remarks: saleToEdit?.remarks || '',
    otherDetails: saleToEdit?.otherDetails || ''
  });

  const [message, setMessage] = useState('');
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [topSuggestions, setTopSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [showRemarksForm, setShowRemarksForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProductStock, setSelectedProductStock] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [upiData, setUpiData] = useState({ name: '', id: '', amount: '' });
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [useCommonTerms, setUseCommonTerms] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [useIGST, setUseIGST] = useState(false);
  const [originalUnitRates, setOriginalUnitRates] = useState({}); // Track original rates for unit conversions
  const suggestionRefs = useRef([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Unit conversion logic
  const getUnitConversionRate = (fromUnit, toUnit) => {
    const unitMap = {
      'kg': { 'gm': 1000, 'kg': 1 },
      'gm': { 'kg': 1/1000, 'gm': 1 },
      'ltr': { 'ml': 1000, 'ltr': 1 },
      'ml': { 'ltr': 1/1000, 'ml': 1 }
    };
    
    return unitMap[fromUnit]?.[toUnit] || 1;
  };

  const convertUnitRate = (originalRate, originalUnit, newUnit) => {
    if (!originalRate || !originalUnit || !newUnit) return originalRate;
    
    const conversionRate = getUnitConversionRate(originalUnit, newUnit);
    return (parseFloat(originalRate) / conversionRate).toFixed(2);
  };

  const convertQuantity = (originalQty, originalUnit, newUnit) => {
    if (!originalQty || !originalUnit || !newUnit) return originalQty;
    
    const conversionRate = getUnitConversionRate(originalUnit, newUnit);
    return (parseFloat(originalQty) * conversionRate).toFixed(2);
  };

  // Validation functions
  const validateUpiName = (name) => {
    // Only allow letters and spaces (First Name & Last Name)
    const nameRegex = /^[a-zA-Z\s]{1,50}$/;
    return nameRegex.test(name);
  };

  const validateUpiId = (id) => {
    // Only allow special symbols (@ . _) and numbers
    const upiRegex = /^[0-9@._]+$/;
    return upiRegex.test(id);
  };

  const validateDateFormat = (date) => {
    // Check if date is in DD-MM-YYYY format
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (!dateRegex.test(date)) return false;
    
    const parts = date.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    if (day < 1 || day > 31) return false;
    if (month < 1 || month > 12) return false;
    if (year < 1900 || year > 2100) return false;
    
    return true;
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        handleThermalBill();
      } else if (e.altKey && e.key === 'a') {
        e.preventDefault();
        handleA4Bill();
      } else if (e.altKey && e.key === 'n') {
        e.preventDefault();
        handleSend();
      } else if (e.altKey && e.key === 'i') {
        e.preventDefault();
        setShowCustomerForm(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [formData.totals.netAmount]);

  // Fetch current user's name from employee data or profile
  useEffect(() => {
    if (!currentUser) {
      setCurrentUserName('');
      setUserProfile(null);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);

    // First, check if user has a profile (for employees)
    const userProfileRef = ref(database, `users/${encodedEmail}/profile`);
    const unsubscribeProfile = onValue(userProfileRef, (snapshot) => {
      const profileData = snapshot.val();
      if (profileData) {
        setUserProfile(profileData);
        if (profileData.personalInfo && profileData.personalInfo.name) {
          setCurrentUserName(profileData.personalInfo.name);
          // Update formData if not editing an existing sale
          if (!saleToEdit) {
            setFormData(prev => ({ ...prev, salesMan: profileData.personalInfo.name }));
          }
          return;
        }
      }

      // If no profile found or no name in profile, check if user is in someone's employee list
      const checkAsEmployee = async () => {
        try {
          // Get all users and check their employee lists
          const usersRef = ref(database, 'users');
          const usersSnapshot = await get(usersRef);
          
          if (usersSnapshot.exists()) {
            const users = usersSnapshot.val();
            let foundEmployeeName = null;

            // Iterate through all users to find if current user is an employee
            for (const [userKey, userData] of Object.entries(users)) {
              if (userData.employees) {
                // Check if current user email exists in this user's employees
                for (const [employeeId, employeeData] of Object.entries(userData.employees)) {
                  if (employeeData.email === currentUser.email) {
                    foundEmployeeName = employeeData.name;
                    break;
                  }
                }
                if (foundEmployeeName) break;
              }
            }

            if (foundEmployeeName) {
              setCurrentUserName(foundEmployeeName);
              if (!saleToEdit) {
                setFormData(prev => ({ ...prev, salesMan: foundEmployeeName }));
              }
            } else {
              // If not found as employee, use email prefix as default
              const defaultName = currentUser.email.split('@')[0].toUpperCase();
              setCurrentUserName(defaultName);
              if (!saleToEdit) {
                setFormData(prev => ({ ...prev, salesMan: defaultName }));
              }
            }
          }
        } catch (error) {
          console.error('Error checking employee data:', error);
          // Fallback to email prefix
          const defaultName = currentUser.email.split('@')[0].toUpperCase();
          setCurrentUserName(defaultName);
          if (!saleToEdit) {
            setFormData(prev => ({ ...prev, salesMan: defaultName }));
          }
        }
      };

      checkAsEmployee();
    }, (error) => {
      console.error('Error fetching user profile:', error);
      // Fallback to email prefix
      if (currentUser) {
        const defaultName = currentUser.email.split('@')[0].toUpperCase();
        setCurrentUserName(defaultName);
        if (!saleToEdit) {
          setFormData(prev => ({ ...prev, salesMan: defaultName }));
        }
      }
    });

    return () => unsubscribeProfile();
  }, [currentUser, saleToEdit]);

  // Update bill number
  useEffect(() => {
    if (!currentUser || saleToEdit) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const salesRef = ref(database, `users/${encodedEmail}/sales`);

    get(salesRef)
      .then((snapshot) => {
        let maxBillNo = 0;
        if (snapshot.exists()) {
          const sales = snapshot.val();
          const billNos = Object.values(sales)
            .map((sale) => parseInt(sale.billNo, 10))
            .filter((num) => !isNaN(num));
          maxBillNo = billNos.length > 0 ? Math.max(...billNos) : 0;
        }
        setFormData((prev) => ({ ...prev, billNo: maxBillNo + 1 || 1 }));
      })
      .catch((error) => {
        console.error('Error fetching billNo:', error);
        setMessage('Failed to load bill number.');
      });
  }, [currentUser, saleToEdit]);

  // Load products
  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const productsRef = ref(database, `users/${encodedEmail}/products`);

    const unsubscribe = onValue(
      productsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const productList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setProducts(productList);
        } else {
          setProducts([]);
          setSelectedProductStock(0);
        }
      },
      (error) => {
        console.error('Error fetching products:', error);
        setMessage('Failed to load products.');
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Load customers
  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const customersRef = ref(database, `users/${encodedEmail}/customers`);

    const unsubscribe = onValue(
      customersRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const customerList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setCustomers(customerList);
        } else {
          setCustomers([]);
        }
      },
      (error) => {
        console.error('Error fetching customers:', error);
        setMessage('Failed to load customers.');
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Handle edit mode
  useEffect(() => {
    if (saleToEdit) {
      setFormData({
        billNo: saleToEdit.billNo,
        date: saleToEdit.date,
        terms: saleToEdit.terms,
        salesMan: saleToEdit.salesMan,
        customer: saleToEdit.customer,
        phone: saleToEdit.phone,
        taxPercent: "",
        items: saleToEdit.items.map((item, index) => ({
          ...item,
          sNo: index + 1,
        })),
        totals: saleToEdit.totals,
        topInput: {
          itemDescription: '',
          qty: '',
          disc: '',
          unitRate: '',
          tax: '',
        },
        remarks: saleToEdit.remarks || '',
        otherDetails: saleToEdit.otherDetails || ''
      });

      const firstItem = saleToEdit.items[0];
      if (firstItem && firstItem.itemDescription) {
        const product = products.find(p => p.productName === firstItem.itemDescription);
        setSelectedProductStock(product ? parseFloat(product.openingStock) || 0 : 0);
      }
    }
  }, [saleToEdit, products]);

  // Update stock display
  useEffect(() => {
    const firstItem = formData.items[0];
    if (firstItem && firstItem.itemDescription) {
      const product = products.find(p => p.productName === firstItem.itemDescription);
      setSelectedProductStock(product ? parseFloat(product.openingStock) || 0 : 0);
    } else {
      setSelectedProductStock(0);
    }
  }, [formData.items, products]);

  // Handle hold data restoration
  useEffect(() => {
    const storedHoldData = localStorage.getItem('holdBillData');
    if (storedHoldData) {
      const holdData = JSON.parse(storedHoldData);
      setFormData(prev => ({
        ...prev,
        billNo: holdData.billNo,
        date: holdData.date,
        terms: holdData.terms,
        salesMan: holdData.salesMan || currentUserName || prev.salesMan,
        customer: holdData.customer,
        phone: holdData.phone,
        items: holdData.items,
        totals: holdData.totals,
        remarks: holdData.remarks,
        otherDetails: holdData.otherDetails
      }));
      localStorage.removeItem('holdBillData');
    }
  }, [currentUserName]);

const calculateTotals = (items, taxPercent) => {
  let grossAmt = 0,
      discount = 0,
      totalTax = 0;

  const updatedItems = items.map((item) => {
    const qty = parseFloat(item.qty) || 0;
    const unitRate = parseFloat(item.unitRate) || 0;
    const discPercent = parseFloat(item.discPercent) || 0;
    const tax = parseFloat(item.tax) || parseFloat(taxPercent) || 0;

    // Calculate gross amount (Unit Rate × Quantity)
    const grossAmount = qty * unitRate;

    // Calculate discount amount
    const itemDiscount = (grossAmount * discPercent) / 100;

    // Net value before tax (Gross Amount - Discount)
    const netValueBeforeTax = grossAmount - itemDiscount;

    // Tax calculation on net value before tax
    const itemTax = (netValueBeforeTax * tax) / 100;

    // Final net value (Gross Amount - Discount + Tax)
    const netValue = netValueBeforeTax + itemTax;

    // Aggregate totals
    grossAmt += grossAmount;
    discount += itemDiscount;
    totalTax += itemTax;

    return {
      ...item,
      netValue: netValue.toFixed(2),
      tax: tax.toString(),
    };
  });

  const netAmount = grossAmt - discount + totalTax;

  return {
    items: updatedItems,
    totals: {
      grossAmt: parseFloat(grossAmt.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      billDiscount: parseFloat(formData.totals.billDiscount || 0),
      totalTax: parseFloat(totalTax.toFixed(2)),
      netAmount: parseFloat(netAmount.toFixed(2)),
    },
  };
};


  const handleChange = (e, index) => {
  const { name, value } = e.target;

  if (name === 'date') {
    // Validate date format
    if (value && !validateDateFormat(value)) {
      setMessage('Please enter date in DD-MM-YYYY format');
      return;
    }
  }

  if (['customer', 'phone', 'terms', 'salesMan', 'date'].includes(name)) {
    setFormData({ ...formData, [name]: value });
    
   if (['customer', 'phone', 'terms', 'salesMan', 'date'].includes(name)) {
  setFormData({ ...formData, [name]: value });

  if (name === 'customer') {
    const selectedCustomer = customers.find(cust =>
      (cust.name === value) || (cust.customerName === value)
    );
    if (selectedCustomer) {
      setFormData(prev => ({
        ...prev,
        phone: selectedCustomer.mobile || selectedCustomer.mobileNo || selectedCustomer.phone || '',
        customerGSTIN: selectedCustomer.gstTinNo || selectedCustomer.gstin || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, phone: '', customerGSTIN: '' }));
    }
  }
}
  } else {
    const updatedItems = [...formData.items];
    updatedItems[index][name] = value;

    if (name === 'itemDescription' && value.trim()) {
      const filtered = products.filter((p) =>
        p.productName && p.productName.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered.slice(0, 5));
      setFocusedItemIndex(index);
      setActiveSuggestionIndex(-1);
    } else if (name === 'itemDescription') {
      setSuggestions([]);
      setFocusedItemIndex(null);
    }

    const { items, totals } = calculateTotals(updatedItems, formData.taxPercent);
    setFormData({ ...formData, items, totals });
  }
};

const handleSelectProduct = (product, index) => {
  if (parseFloat(product.openingStock) <= 0) {
    setMessage('Selected product is out of stock.');
    return;
  }

  const updatedItems = [...formData.items];
  const unitRate = (parseFloat(product.mrp) || 0).toString();
  const productUnit = product.unit || '';
  
  updatedItems[index] = {
    ...updatedItems[index],
    itemDescription: product.productName || '',
    unit: productUnit,
    unitRate: unitRate,
    tax: (parseFloat(product.salesTax) || formData.taxPercent).toString(),
    discPercent: (parseFloat(product.discount) || 0).toString(),
  };

  const { items, totals } = calculateTotals(updatedItems, formData.taxPercent);
  setFormData({ ...formData, items, totals });

  setSuggestions([]);
  setFocusedItemIndex(null);
  setActiveSuggestionIndex(-1);
  setSelectedProductStock(parseFloat(product.openingStock) || 0);
};

  const handleTopInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      topInput: { ...prev.topInput, [name]: value },
    }));

    if (name === 'itemDescription' && value.trim()) {
      const filtered = products.filter((p) =>
        p.productName && p.productName.toLowerCase().includes(value.toLowerCase())
      );
      setTopSuggestions(filtered.slice(0, 5));
    } else if (name === 'itemDescription') {
      setTopSuggestions([]);
    }
  };

const handleTopSelectProduct = (product) => {
  if (parseFloat(product.openingStock) <= 0) {
    setMessage('Selected product is out of stock.');
    return;
  }

  const updatedItems = [...formData.items];
  const firstItem = updatedItems[0];
  // Use MRP as the unit rate, fallback to topInput unitRate if provided
  const unitRate = formData.topInput.unitRate || (parseFloat(product.mrp) || 0).toString();
  const productUnit = product.unit || '';
 
  // Check if first row has data (excluding sNo and netValue)
  const hasDataInFirstRow = firstItem && (
    firstItem.itemDescription ||
    firstItem.unit ||
    firstItem.qty ||
    firstItem.discPercent ||
    firstItem.unitRate ||
    firstItem.tax
  );

  if (hasDataInFirstRow) {
    // Add new row
    const newItem = {
      sNo: formData.items.length + 1,
      itemDescription: product.productName || '',
      unit: productUnit,
      qty: formData.topInput.qty || '1',
      discPercent: formData.topInput.disc || (parseFloat(product.discount) || 0).toString(),
      unitRate: unitRate, // Using MRP
      tax: formData.topInput.tax || (parseFloat(product.salesTax) || formData.taxPercent).toString(),
      netValue: '',
    };
    updatedItems.push(newItem);
    
    // Store original unit rate for the new item
    const itemKey = `${updatedItems.length - 1}_${product.productName}`;
    setOriginalUnitRates(prev => ({
      ...prev,
      [itemKey]: {
        rate: unitRate, // Using MRP
        unit: productUnit
      }
    }));
  } else {
    // Update first row
    updatedItems[0] = {
      ...firstItem,
      itemDescription: product.productName || '',
      unit: productUnit,
      qty: formData.topInput.qty || '1',
      discPercent: formData.topInput.disc || (parseFloat(product.discount) || 0).toString(),
      unitRate: unitRate, // Using MRP
      tax: formData.topInput.tax || (parseFloat(product.salesTax) || formData.taxPercent).toString(),
      netValue: '',
    };
    
    // Store original unit rate for the first item
    const itemKey = `0_${product.productName}`;
    setOriginalUnitRates(prev => ({
      ...prev,
      [itemKey]: {
        rate: unitRate, // Using MRP
        unit: productUnit
      }
    }));
  }

  const { items, totals } = calculateTotals(updatedItems, formData.taxPercent);
  setFormData({
    ...formData,
    items,
    totals,
    topInput: {
      itemDescription: '',
      qty: '',
      disc: '',
      unitRate: '',
      tax: '',
    },
  });

  setTopSuggestions([]);
  setSelectedProductStock(parseFloat(product.openingStock) || 0);
};





  // Update stock display based on the last selected product
  useEffect(() => {
    const lastItem = formData.items[formData.items.length - 1];
    if (lastItem && lastItem.itemDescription) {
      const product = products.find(p => p.productName === lastItem.itemDescription);
      setSelectedProductStock(product ? parseFloat(product.openingStock) || 0 : 0);
    } else if (formData.topInput.itemDescription) {
      const product = products.find(p => p.productName === formData.topInput.itemDescription);
      setSelectedProductStock(product ? parseFloat(product.openingStock) || 0 : 0);
    } else {
      setSelectedProductStock(0);
    }
  }, [formData.items, formData.topInput, products]);
 
  const handleKeyDown = (e, index) => {
    if (focusedItemIndex !== index || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectProduct(suggestions[activeSuggestionIndex], index);
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setFocusedItemIndex(null);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleTopKeyDown = (e) => {
    if (topSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < topSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleTopSelectProduct(topSuggestions[activeSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setTopSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleClickOutside = (e) => {
    if (
      suggestionRefs.current.every((ref) => ref && !ref.contains(e.target)) &&
      !e.target.closest('input[name="itemDescription"]')
    ) {
      setSuggestions([]);
      setTopSuggestions([]);
      setFocusedItemIndex(null);
      setActiveSuggestionIndex(-1);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const addNewItem = () => {
    const newItem = {
      sNo: formData.items.length + 1,
      itemDescription: '',
      unit: '',
      qty: '',
      discPercent: '',
      unitRate: '',
      tax: '',
      netValue: '',
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem],
    });
  };

const updateProductStock = async (items) => {
  if (!currentUser) {
    console.error('No current user for updating product stock');
    return;
  }

  const encodedEmail = encodeEmailForPath(currentUser.email);

  try {
    // Use Promise.all to handle all updates simultaneously
    await Promise.all(
      items.map(async (item) => {
        if (!item.itemDescription || !item.qty) return; // Skip empty items
        
        const product = products.find(p => p.productName === item.itemDescription);
        if (!product) {
          console.warn(`Product not found: ${item.itemDescription}`);
          return;
        }

        const qtySold = parseFloat(item.qty) || 0;
        const currentStock = parseFloat(product.openingStock) || 0;
        const newStock = Math.max(0, currentStock - qtySold);

        console.log(`Updating stock for ${product.productName}: ${currentStock} - ${qtySold} = ${newStock}`);

        const productRef = ref(database, `users/${encodedEmail}/products/${product.id}`);
        await update(productRef, { 
          openingStock: newStock.toString(),
          updatedAt: new Date().toISOString()
        });
        
        console.log(`Stock updated successfully for ${product.productName}`);
      })
    );
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw new Error('Failed to update product stock: ' + error.message);
  }
};

const saveSale = async () => {
  if (!currentUser) {
    console.error('No user is logged in for saving sale');
    throw new Error('No user is logged in.');
  }

  const encodedEmail = encodeEmailForPath(currentUser.email);
  
  // Filter out items with no description or quantity
  const validItems = formData.items.filter(item => 
    item.itemDescription && item.qty && parseFloat(item.qty) > 0
  );

  if (validItems.length === 0) {
    throw new Error('Please add at least one valid item with quantity');
  }

  const itemsWithUpdatedSNo = validItems.map((item, index) => ({
    ...item,
    sNo: index + 1,
  }));

const saleData = {
  billNo: formData.billNo,
  date: formData.date,
  terms: formData.terms,
  salesMan: currentUserName || formData.salesMan,
  customer: formData.customer,
  phone: formData.phone || "",
  customerGSTIN: formData.customerGSTIN || "",
  items: itemsWithUpdatedSNo.map(item => ({
    ...item,
    productName: item.itemDescription,
    pRate: item.unitRate,
    discAmt: ((parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0) * (parseFloat(item.discPercent) || 0) / 100).toFixed(2),
    cgstRate: useIGST ? 0 : (parseFloat(item.tax) || 0) / 2,
    sgstRate: useIGST ? 0 : (parseFloat(item.tax) || 0) / 2,
    igstRate: useIGST ? (parseFloat(item.tax) || 0) : 0,
    cessRate: 0,
    grossAmt: ((parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0)).toFixed(2),
    netValue: item.netValue, // This is the correctly calculated net value from the form
    hsnCode: item.hsnCode || '',
    groupName: item.groupName || '',
  })),
  totals: formData.totals,
  userEmail: currentUser.email,
  createdAt: new Date().toISOString(),
  status: 'completed',
  remarks: formData.remarks,
  otherDetails: formData.otherDetails
};

  try {
    console.log('Saving sale data:', saleData);
    
    // Update stock first, then save sale
    await updateProductStock(itemsWithUpdatedSNo);

    let newSaleId = null;
    if (saleToEdit && saleToEdit.id) {
      // For editing, first restore the old stock, then deduct new stock
      if (saleToEdit.items) {
        // Restore old stock
        await restoreProductStock(saleToEdit.items);
      }
      
      const saleRef = ref(database, `users/${encodedEmail}/sales/${saleToEdit.id}`);
      await set(saleRef, saleData);
      newSaleId = saleToEdit.id;
      console.log(`Sale updated successfully with ID: ${newSaleId}`);
    } else {
      const salesRef = ref(database, `users/${encodedEmail}/sales`);
      const newSaleRef = await push(salesRef);
      await set(newSaleRef, saleData);
      newSaleId = newSaleRef.key;
      console.log(`Sale saved successfully with ID: ${newSaleId}`);
    }

    return { message: 'Sales data saved successfully!', saleData: { ...saleData, id: newSaleId } };
  } catch (error) {
    console.error('Error saving sale:', error);
    throw new Error(`Failed to save sale: ${error.message}`);
  }
};

// Add this new function for restoring stock when editing sales:
const restoreProductStock = async (items) => {
  if (!currentUser) return;

  const encodedEmail = encodeEmailForPath(currentUser.email);

  try {
    await Promise.all(
      items.map(async (item) => {
        if (!item.itemDescription || !item.qty) return;
        
        const product = products.find(p => p.productName === item.itemDescription);
        if (!product) return;

        const qtyToRestore = parseFloat(item.qty) || 0;
        const currentStock = parseFloat(product.openingStock) || 0;
        const restoredStock = currentStock + qtyToRestore;

        const productRef = ref(database, `users/${encodedEmail}/products/${product.id}`);
        await update(productRef, { 
          openingStock: restoredStock.toString(),
          updatedAt: new Date().toISOString()
        });
        
        console.log(`Stock restored for ${product.productName}: ${currentStock} + ${qtyToRestore} = ${restoredStock}`);
      })
    );
  } catch (error) {
    console.error('Error restoring product stock:', error);
  }
};

  const handleSave = async () => {
     try {
    const { message, saleData } = await saveSale();
    setMessage(message);
    alert('Sale saved successfully!'); // Display alert after successful save
    onSubmit(saleData); // Pass saleData to onSubmit
    handleRefresh();
  } catch (error) {
    setMessage(`Error: ${error.message}`);
    alert(`Error: ${error.message}`); // Display alert for error
  }
};
  const handleCancel = () => {
    handleRefresh();
    onClose();
  };

  const handleHold = () => {
    if (formData.totals.netAmount <= 0) {
      setMessage('Please add items before holding the bill.');
      console.warn('Hold attempted with zero net amount');
      return;
    }

    const holdData = {
      billNo: formData.billNo,
      date: formData.date,
      terms: formData.terms,
      salesMan: currentUserName || formData.salesMan,
      customer: formData.customer,
      phone: formData.phone,
      items: formData.items,
      totals: formData.totals,
      remarks: formData.remarks,
      otherDetails: formData.otherDetails
    };

    localStorage.setItem('holdBillData', JSON.stringify(holdData));
    window.open(window.location.href, '_blank');
    handleRefresh();
  };

  const handlePrintBill = (type) => {
    // Print functionality
    const printContent = `
      <div style="font-family: monospace; font-size: 12px; width: ${type === 'thermal' ? '300px' : '100%'};">
        <div style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px;">
          <h2>SALES INVOICE</h2>
          <p>Bill No: ${formData.billNo} | Date: ${formData.date}</p>
          <p>Salesman: ${formData.salesMan}</p>
        </div>
        
        <div style="margin-bottom: 10px;">
          <p><strong>Customer:</strong> ${formData.customer || 'N/A'}</p>
          <p><strong>Phone:</strong> ${formData.phone || 'N/A'}</p>
          <p><strong>Terms:</strong> ${formData.terms}</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
          <thead>
            <tr style="border-bottom: 1px solid #000;">
              <th style="text-align: left; padding: 5px;">Item</th>
              <th style="text-align: right; padding: 5px;">Qty</th>
              <th style="text-align: right; padding: 5px;">Rate</th>
              <th style="text-align: right; padding: 5px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${formData.items.map(item => `
              <tr style="border-bottom: 1px dotted #000;">
                <td style="padding: 3px;">${item.itemDescription}</td>
                <td style="text-align: right; padding: 3px;">${item.qty}</td>
                <td style="text-align: right; padding: 3px;">₹${item.unitRate}</td>
                <td style="text-align: right; padding: 3px;">₹${item.netValue}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="border-top: 2px solid #000; padding-top: 10px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span>Gross Amount:</span>
            <span>₹${formData.totals.grossAmt.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span>Tax Amount:</span>
            <span>₹${formData.totals.totalTax.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-weight: bold; font-size: 14px;">
            <span>Net Amount:</span>
            <span>₹${formData.totals.netAmount.toFixed(2)}</span>
          </div>
          
          ${paymentMethod === 'UPI' ? `
            <div style="margin-top: 10px;">
              <p><strong>Payment Method:</strong> UPI</p>
              <p><strong>UPI Name:</strong> ${upiData.name}</p>
              <p><strong>UPI ID:</strong> ${upiData.id}</p>
              <p><strong>Amount:</strong> ₹${upiData.amount}</p>
            </div>
          ` : paymentMethod === 'CASH' ? `
            <div style="margin-top: 10px;">
              <p><strong>Payment Method:</strong> CASH</p>
              <p><strong>Received:</strong> ₹${receivedAmount.toFixed(2)}</p>
            </div>
          ` : ''}
        </div>
        
        <div style="text-align: center; margin-top: 20px; font-size: 10px;">
          <p>Thank you for your business!</p>
          ${formData.remarks ? `<p>Remarks: ${formData.remarks}</p>` : ''}
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>Print Bill</title></head>
        <body onload="window.print(); window.close();">
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleThermalBill = async () => {
    if (formData.totals.netAmount <= 0) {
      setMessage('Please add items before proceeding to checkout.');
      return;
    }
    setShowCheckoutForm(true);
    setPaymentMethod('UPI');
    setUpiData({ name: '', id: '', amount: formData.totals.netAmount.toString() });
    setReceivedAmount(formData.totals.netAmount);
  };

  const handleA4Bill = async () => {
    if (formData.totals.netAmount <= 0) {
      setMessage('Please add items before proceeding to checkout.');
      return;
    }
    setShowCheckoutForm(true);
    setPaymentMethod('UPI');
    setUpiData({ name: '', id: '', amount: formData.totals.netAmount.toString() });
    setReceivedAmount(formData.totals.netAmount);
  };

  const handleSend = async () => {
    if (formData.totals.netAmount <= 0) {
      setMessage('Please add items before proceeding to checkout.');
      return;
    }
    setShowCheckoutForm(true);
    setPaymentMethod('UPI');
    setUpiData({ name: '', id: '', amount: formData.totals.netAmount.toString() });
    setReceivedAmount(formData.totals.netAmount);
  };

  const handleRemarks = () => {
    setShowRemarksForm(true);
  };

  const handleRemarksSave = () => {
    setShowRemarksForm(false);
    setUseCommonTerms(false);
  };

  const handleRemarksCancel = () => {
    setShowRemarksForm(false);
    setUseCommonTerms(false);
    setFormData(prev => ({
      ...prev,
      remarks: saleToEdit?.remarks || '',
      otherDetails: saleToEdit?.otherDetails || ''
    }));
  };

  const handleRefresh = () => {
    setFormData({
      billNo: formData.billNo + 1,
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      terms: 'CASH',
      salesMan: currentUserName || 'SALES PERSON',
      customer: '',
      phone: '',
      taxPercent: 24,
      items: [
        {
          sNo: 1,
          itemDescription: '',
          unit: '',
          qty: '',
          discPercent: '',
          unitRate: '',
          tax: '',
          netValue: '',
        },
      ],
      totals: {
        grossAmt: 0,
        discount: 0,
        billDiscount: 0,
        totalTax: 0,
        netAmount: 0,
      },
      topInput: {
        itemDescription: '',
        qty: '',
        disc: '',
        unitRate: '',
        tax: '',
      },
      remarks: '',
      otherDetails: ''
    });

    setMessage('');
    setSuggestions([]);
    setTopSuggestions([]);
    setFocusedItemIndex(null);
    setActiveSuggestionIndex(-1);
    setSelectedProductStock(0);
    setShowCheckoutForm(false);
    setShowRemarksForm(false);
    setPaymentMethod('');
    setUpiData({ name: '', id: '', amount: '' });
    setReceivedAmount(0);
    setUseCommonTerms(false);
  };

  const handlePaymentMethod = (method) => {
    setPaymentMethod(method);
    if (method === 'UPI') {
      setUpiData({ name: '', id: '', amount: formData.totals.netAmount.toString() });
      setReceivedAmount(formData.totals.netAmount);
    } else if (method === 'CASH') {
      setReceivedAmount(formData.totals.netAmount);
    } else if (method === 'CREDIT') {
      setReceivedAmount(0);
      setShowCustomerForm(true);
    }
  };

  const handleUpiInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'name') {
      if (!validateUpiName(value) && value !== '') {
        setMessage('UPI Name should only contain letters and spaces (First Name & Last Name)');
        return;
      }
    }
    
    if (name === 'id') {
      if (!validateUpiId(value) && value !== '') {
        setMessage('UPI ID should only contain numbers and symbols (@ . _)');
        return;
      }
    }
    
    setUpiData(prev => ({ ...prev, [name]: value }));
    if (name === 'amount') {
      setReceivedAmount(parseFloat(value) || 0);
    }
  };

  const handleKeypadPress = (key) => {
    let newAmount = upiData.amount;
    if (key === '←') {
      newAmount = newAmount.slice(0, -1);
    } else if (key === '.') {
      if (!newAmount.includes('.')) {
        newAmount = newAmount + key;
      }
    } else {
      newAmount = newAmount + key;
    }
    setUpiData({ ...upiData, amount: newAmount });
    setReceivedAmount(parseFloat(newAmount) || 0);
  };

  const handleClear = () => {
    setUpiData({ name: '', id: '', amount: '' });
    setReceivedAmount(0);
  };

  const handleTakeBill = async () => {
    try {
      const { message } = await saveSale();
      setMessage(message);
      
      // Print the bill
      const billType = paymentMethod === 'UPI' ? 'thermal' : 'a4';
      handlePrintBill(billType);
      
      setShowCheckoutForm(false);
      setPaymentMethod('');
      setUpiData({ name: '', id: '', amount: '' });
      setReceivedAmount(0);
      handleRefresh();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '1rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      margin: '0 auto',
      maxWidth: '1400px',
    },
    message: {
      padding: '0.75rem 1rem',
      marginBottom: '1.5rem',
      backgroundColor: message.includes('Error') ? '#fee2e2' : '#dcfce7',
      color: message.includes('Error') ? '#991b1b' : '#166534',
      border: `1px solid ${message.includes('Error') ? '#fecaca' : '#bbf7d0'}`,
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: '500',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1.25rem 1.5rem',
      color: '#000',
      marginBottom: '1.5rem',
      borderRadius: '8px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
      flexWrap: 'wrap',
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    inputGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    label: {
      fontSize: '0.875rem',
      color: '#000',
      fontWeight: '500',
    },
    input: {
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      transition: 'border-color 0.2s ease',
    },
    select: {
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    customerSelectGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    customerSelect: {
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      width: '200px',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    iconButton: {
      backgroundColor: '#1d4ed8',
      color: '#ffffff',
      padding: '0.5rem',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '600',
      width: '2.5rem',
      height: '2.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(29, 78, 216, 0.3)',
    },
    formSection: {
      backgroundColor: '#f8fafc',
      padding: '1.25rem',
      borderRadius: '8px',
      marginBottom: '1.5rem',
      border: '1px solid #e2e8f0',
    },
    topInputRow: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1rem',
      position: 'relative',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    topInput: {
      flex: 1,
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease',
      minWidth: '200px',
    },
    topSelect: {
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      width: '100px',
    },
    topInputSmall: {
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
      width: '100px',
      transition: 'border-color 0.2s ease',
    },
      tableContainer: {
      height: '400px', // fixed height for better UX
  marginTop: '1rem',
  overflowX: 'auto',      // only horizontal sliding
  overflowY: 'hidden',    // no vertical scroll
  whiteSpace: 'nowrap',   // keeps items in a single row
  scrollBehavior: 'smooth', // makes slide smooth
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
},
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.75rem',
      minWidth: '1200px',
    },
    th: {
      padding: '0.75rem 0.5rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
      backgroundColor: '#ffffff',
      position: 'relative',
    },
    tdInput: {
      width: '100%',
      padding: '0.375rem 0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease',
    },
    suggestions: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '100%',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      zIndex: 1000,
      maxHeight: '300px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    suggestionTable: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    suggestionTh: {
      padding: '0.5rem',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textAlign: 'center',
      border: '1px solid #1e40af',
    },
    suggestionTd: {
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      fontSize: '0.75rem',
      textAlign: 'center',
    },
    suggestionItem: {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    suggestionActive: {
      backgroundColor: '#e0f2fe',
    },
    newBillButton: {
      marginTop: '1rem',
      padding: '0.75rem 1.5rem',
      borderRadius: '6px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    },
    totalsSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: '1.25rem',
      gap: '2rem',
      flexWrap: 'wrap',
    },
    actionButtons: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
    },
    totalsBox: {
      backgroundColor: '#ffffff',
      padding: '1.25rem',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      minWidth: '200px',
      marginLeft:'80%',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    totalsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.75rem',
      fontSize: '0.875rem',
      color: '#374151',
    },
    netAmountRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderRadius: '6px',
      fontWeight: 'bold',
      fontSize: '1rem',
      marginTop: '0.75rem',
      boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
    },
    actionButton: {
      padding: '0.75rem 1.25rem',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      color: '#ffffff',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    thermalButton: {
      backgroundColor: '#dc2626',
    },
    a4Button: {
      backgroundColor: '#6b7280',
    },
    sendButton: {
      backgroundColor: '#6b7280',
    },
    customerButton: {
      backgroundColor: '#6b7280',
    },
    refreshButton: {
      backgroundColor: '#6b7280',
    },
    remarksButton: {
      backgroundColor: '#6b7280',
    },
    holdButton: {
      backgroundColor: '#f59e0b',
    },
    saveButton: {
      backgroundColor: '#10b981',
    },
    cancelButton: {
      backgroundColor: '#ef4444',
    },
    saveCancelSection: {
      display: 'flex',
      gap: '0.75rem',
      marginTop: '1.5rem',
      justifyContent: 'flex-end',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999,
    },
    checkoutModal: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#ffffff',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      zIndex: 1000,
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    remarksModal: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#ffffff',
      padding: '0',
      borderRadius: '12px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      zIndex: 1000,
      width: '90%',
      maxWidth: '500px',
      maxHeight: '80vh',
      overflowY: 'auto',
    },
    remarksHeader: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      padding: '1rem 1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '1.125rem',
      fontWeight: 'bold',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
    closeButton: {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#ffffff',
      padding: '0',
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s ease',
    },
    closeButtonRed: {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      padding: '0',
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s ease',
    },
    remarksContent: {
      padding: '1.5rem',
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginBottom: '1.5rem',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      fontWeight: '500',
    },
    radioInput: {
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    textareaGroup: {
      marginBottom: '1.5rem',
    },
    textareaLabel: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
    },
    textarea: {
      width: '100%',
      height: '100px',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      resize: 'vertical',
      boxSizing: 'border-box',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease',
    },
    remarksButtons: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
    },
    checkoutHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #e2e8f0',
    },
    checkoutTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
    },
    amountSection: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '2rem',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    amountBox: {
      flex: 1,
      textAlign: 'center',
      padding: '1.5rem',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      minWidth: '150px',
    },
    amountLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: '600',
    },
    amountValue: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
    },
    paymentOptions: {
      display: 'flex',
      gap: '0.75rem',
      marginBottom: '2rem',
      flexWrap: 'wrap',
    },
    paymentButton: {
      flex: 1,
      padding: '1rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: '2px solid transparent',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      minWidth: '120px',
    },
    paymentButtonActive: {
      backgroundColor: '#10b981',
      color: '#ffffff',
      borderColor: '#059669',
    },
    paymentButtonInactive: {
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      borderColor: '#d1d5db',
    },
    upiSection: {
      marginBottom: '2rem',
    },
    upiInputGroup: {
      marginBottom: '1rem',
    },
    upiLabel: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
    },
    upiInput: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s ease',
    },
    keypadSection: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '2rem',
      flexWrap: 'wrap',
    },
    keypad: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '0.5rem',
      flex: 1,
      minWidth: '200px',
    },
    keyButton: {
      padding: '1rem',
      borderRadius: '8px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: '2px solid #e2e8f0',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60px',
    },
    clearButton: {
      padding: '1rem 2rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: '2px solid #dc2626',
      backgroundColor: '#ffffff',
      color: '#dc2626',
      alignSelf: 'center',
      transition: 'all 0.2s ease',
    },
    takeBillButton: {
      width: '100%',
      padding: '1rem 2rem',
      borderRadius: '8px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: 'none',
      backgroundColor: '#10b981',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.3s ease',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    },
    netAmountDisplay: {
      marginTop: '1rem',
      fontSize: '1.125rem',
      fontWeight: 'bold',
      color: '#1f2937',
      textAlign: 'right',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1.125rem' }}>
          <h2>Please login to access the Sales Form</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        {message && <div style={styles.message}>{message}</div>}

        <div style={styles.header}>
          <div style={styles.headerLeft}>
           <div style={styles.inputGroup}>
  <label style={styles.label}>Date:</label>
  <input
    type="date"
    name="date"
    value={formData.date.split('-').reverse().join('-')} // Convert DD-MM-YYYY to YYYY-MM-DD for input
    onChange={(e) => {
      const selectedDate = e.target.value;
      if (selectedDate) {
        // Convert YYYY-MM-DD to DD-MM-YYYY for formData
        const [year, month, day] = selectedDate.split('-');
        setFormData({ ...formData, date: `${day}-${month}-${year}` });
      } else {
        setFormData({ ...formData, date: '' });
      }
    }}
    style={styles.input}
  />
</div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>PTR:</label>
              <select
                name="terms"
                value={formData.terms}
                onChange={(e) => handleChange(e)}
                style={styles.select}
              >
                <option value="CASH">CASH</option>
                <option value="CREDIT">CREDIT</option>
              </select>
            </div>
         
            <div style={styles.inputGroup}>
              <label style={styles.label}>STOCK:</label>
              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{selectedProductStock}</span>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>QTY:</label>
              <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                {formData.items.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0)}
              </span>
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.customerSelectGroup}>
              <select
                name="customer"
                value={formData.customer}
                onChange={(e) => handleChange(e)}
                style={styles.customerSelect}
              >
                <option value="">Choose Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.name || customer.customerName}>
                    {customer.name || customer.customerName}
                  </option>
                ))}
              </select>
              <button
                style={styles.iconButton}
                onClick={() => setShowCustomerForm(true)}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(29, 78, 216, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(29, 78, 216, 0.3)';
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div style={styles.formSection}>
          <div style={styles.topInputRow}>
            <input
              type="text"
              name="itemDescription"
              value={formData.topInput.itemDescription}
              onChange={handleTopInputChange}
              onKeyDown={handleTopKeyDown}
              style={styles.topInput}
              placeholder="Enter Item Name"
            />
            {topSuggestions.length > 0 && (
              <div style={styles.suggestions}>
                <table style={styles.suggestionTable}>
                  <thead>
                    <tr>
                      <th style={styles.suggestionTh}>CODE</th>
                      <th style={styles.suggestionTh}>PRODUCT</th>
                      <th style={styles.suggestionTh}>RACK</th>
                      <th style={styles.suggestionTh}>BRAND</th>
                      <th style={styles.suggestionTh}>S.PRICE</th>
                      <th style={styles.suggestionTh}>QTY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSuggestions.map((product, idx) => (
                      <tr
                        key={product.id}
                        style={{
                          ...styles.suggestionItem,
                          ...(activeSuggestionIndex === idx ? styles.suggestionActive : {}),
                        }}
                        onClick={() => handleTopSelectProduct(product)}
                        onMouseEnter={() => setActiveSuggestionIndex(idx)}
                      >
<td style={styles.suggestionTd}>{products.findIndex(p => p.id === product.id) + 1}</td>                        <td style={styles.suggestionTd}>{product.productName || 'Unknown Product'}</td>
                        <td style={styles.suggestionTd}>{product.rack || 'N/A'}</td>
                        <td style={styles.suggestionTd}>{product.brand || 'GENERAL'}</td>
                        <td style={styles.suggestionTd}>₹{parseFloat(product.sellingRate || 0).toFixed(2)}</td>
                        <td style={styles.suggestionTd}>{product.openingStock || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <select
              name="qty"
              value={formData.topInput.qty}
              onChange={handleTopInputChange}
              style={styles.topSelect}
            >
              <option value="">Select Qty</option>
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
            <input
              type="number"
              name="disc"
              value={formData.topInput.disc}
              onChange={handleTopInputChange}
              style={styles.topInputSmall}
              placeholder="Select Percentage"
              min="0"
              max="100"
            />
            <input
              type="number"
              name="unitRate"
              value={formData.topInput.unitRate}
              onChange={handleTopInputChange}
              style={styles.topInputSmall}
              placeholder="S.Price"
              min="0"
              step="0.01"
            />
            <select
              name="tax"
              value={formData.topInput.tax}
              onChange={handleTopInputChange}
              style={styles.topSelect}
            >
                <option value="">Select Tax</option>
              <option value="">0</option>
              <option value="0">0%</option>
              <option value="12">12%</option>
              <option value="24">24%</option> 
            </select>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>S.NO</th>
                <th style={styles.th}>ITEM DESCRIPTION</th>
                <th style={styles.th}>UNIT</th>
                <th style={styles.th}>QTY</th>
                <th style={styles.th}>DISC%</th>
                <th style={styles.th}>UNIT RATE</th>
                <th style={styles.th}>TAX</th>
                <th style={styles.th}>GROSS AMOUNT</th>
                <th style={styles.th}>NET VALUE</th>
              </tr>
            </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white' }}>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#2563eb' }}>{index + 1}</td>
                    <td style={styles.td}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          name="itemDescription"
                          value={item.itemDescription}
                          onChange={(e) => handleChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          style={styles.tdInput}
                          placeholder="Search product..."
                        />
                        {focusedItemIndex === index && suggestions.length > 0 && (
                          <div style={styles.suggestions}>
                            <table style={styles.suggestionTable}>
                              <thead>
                                <tr>
                                  <th style={styles.suggestionTh}>CODE</th>
                                  <th style={styles.suggestionTh}>PRODUCT</th>
                                  <th style={styles.suggestionTh}>RACK</th>
                                  <th style={styles.suggestionTh}>BRAND</th>
                                  <th style={styles.suggestionTh}>S.PRICE</th>
                                  <th style={styles.suggestionTh}>QTY</th>
                                </tr>
                              </thead>
                              <tbody>
                                {suggestions.map((product, idx) => (
                                  <tr
                                    key={product.id}
                                    ref={(el) => (suggestionRefs.current[idx] = el)}
                                    style={{
                                      ...styles.suggestionItem,
                                      ...(activeSuggestionIndex === idx ? styles.suggestionActive : {}),
                                    }}
                                    onClick={() => handleSelectProduct(product, index)}
                                    onMouseEnter={() => setActiveSuggestionIndex(idx)}
                                  >
                                 <td style={styles.suggestionTd}>{products.findIndex(p => p.id === product.id) + 1}</td>
                                    <td style={styles.suggestionTd}>{product.productName || 'Unknown Product'}</td>
                                    <td style={styles.suggestionTd}>{product.rack || 'N/A'}</td>
                                    <td style={styles.suggestionTd}>{product.brand || 'GENERAL'}</td>
                                    <td style={styles.suggestionTd}>₹{parseFloat(product.sellingRate || 0).toFixed(2)}</td>
                                    <td style={styles.suggestionTd}>{product.openingStock || '0'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </td>
                <td style={styles.td}>
  <input
    type="text"
    name="unit"
    value={item.unit}
    onChange={(e) => handleChange(e, index)}
    style={styles.tdInput}

  />
</td>
                    <td style={styles.td}>
                      <input
                        type="number"
                        name="qty"
                        value={item.qty}
                        onChange={(e) => handleChange(e, index)}
                        style={styles.tdInput}
                        min="0"
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        type="number"
                        name="discPercent"
                        value={item.discPercent}
                        onChange={(e) => handleChange(e, index)}
                        style={styles.tdInput}
                        min="0"
                        max="100"
                      />
                    </td>
                <td style={styles.td}>

  <input
    type="number"
    name="unitRate"
    value={item.unitRate}
    onChange={(e) => handleChange(e, index)}
    style={styles.tdInput}
    min="0"
    step="0.01"
    placeholder="Unit Rate"
  />
</td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        name="tax"
                        value={item.tax}
                        onChange={(e) => handleChange(e, index)}
                        style={styles.tdInput}
                        min="0"
                        step="0.01"
                      />
                    </td>
              



<td style={styles.td}>

  ₹{((parseFloat(item.qty) || 0) * (parseFloat(item.unitRate) || 0)).toFixed(2)}
</td>


<td style={styles.td}>

  ₹{item.netValue}
</td>

               
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         
          <button
            style={styles.newBillButton}
            onClick={addNewItem}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
            }}
          >
            + New Line
          </button>
        </div>

        <div style={styles.totalsSection}>
          <div style={styles.actionButtons}>
            {[
              // { text: 'THERMAL BILL (F2)', handler: handleThermalBill, style: styles.thermalButton },
              // { text: 'A4 BILL (ALT + A)', handler: handleA4Bill, style: styles.a4Button },
              // { text: 'SEND (ALT + N)', handler: handleSend, style: styles.sendButton },
              { text: 'CUSTOMER (ALT + I)', handler: () => setShowCustomerForm(true), style: styles.customerButton },
              { text: 'REFRESH (F5)', handler: handleRefresh, style: styles.refreshButton },
              { text: 'REMARKS', handler: handleRemarks, style: styles.remarksButton },
              // { text: 'HOLD', handler: handleHold, style: styles.holdButton },
            ].map((button, index) => (
              <button
                key={index}
                style={{ ...styles.actionButton, ...button.style }}
                onClick={button.handler}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
          <div style={styles.totalsBox}>
            <div style={styles.totalsRow}>
              <label>Bill No:</label>
              <span>{formData.billNo}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Gross Amt:</label>
              <span>₹{formData.totals.grossAmt.toFixed(2)}</span>
            </div>
            <div style={styles.totalsRow}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                IGST Amt:
                <input
                  type="checkbox"
                  checked={useIGST}
                  onChange={(e) => setUseIGST(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
              </label>
              <span>₹{useIGST ? formData.totals.totalTax.toFixed(2) : '0.00'}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>CGST Amt:</label>
              <span>₹{useIGST ? '0.00' : (formData.totals.totalTax / 2).toFixed(2)}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>SGST Amt:</label>
              <span>₹{useIGST ? '0.00' : (formData.totals.totalTax / 2).toFixed(2)}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Cess Amt:</label>
              <span>₹0.00</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Net Price:</label>
              <span>₹{formData.totals.netAmount.toFixed(2)}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Rounded Off:</label>
              <span>₹0.00</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Product Discount:</label>
              <span>₹{formData.totals.discount.toFixed(2)}</span>
            </div>
            <div style={styles.totalsRow}>
              <label>Discount:</label>
              <span>₹{formData.totals.billDiscount.toFixed(2)}</span>
            </div>
            <div style={styles.netAmountRow}>
              <label>Net Amount:</label>
              <span>₹{formData.totals.netAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div style={styles.saveCancelSection}>
          <button
            style={{ ...styles.actionButton, ...styles.saveButton }}
            onClick={handleSave}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
            }}
          >
            ✅ SAVE
          </button>
          <button
            style={{ ...styles.actionButton, ...styles.cancelButton }}
            onClick={handleCancel}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
            }}
          >
            ❌ CANCEL
          </button>
        </div>

        <div style={styles.netAmountDisplay}>
          Net Amount: ₹{formData.totals.netAmount.toFixed(2)}
        </div>

        {showCheckoutForm && (
          <>
            <div style={styles.overlay} onClick={() => setShowCheckoutForm(false)} />
            <div style={styles.checkoutModal}>
              <div style={styles.checkoutHeader}>
                <h2 style={styles.checkoutTitle}>🛒 CHECKOUT PAGE</h2>
                <button
                  style={styles.closeButtonRed}
                  onClick={() => setShowCheckoutForm(false)}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#dc2626')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#ef4444')}
                >
                  ×
                </button>
              </div>

              <div style={styles.amountSection}>
                <div style={styles.amountBox}>
                  <div style={styles.amountLabel}>TOTAL AMOUNT</div>
                  <div style={styles.amountValue}>{formData.totals.netAmount.toFixed(2)}</div>
                </div>
                <div style={styles.amountBox}>
                  <div style={styles.amountLabel}>RECEIVED AMOUNT</div>
                  <div style={styles.amountValue}>{receivedAmount.toFixed(2)}</div>
                </div>
                <div style={styles.amountBox}>
                  <div style={styles.amountLabel}>BALANCE AMOUNT</div>
                  <div style={{
                    ...styles.amountValue,
                    color: (receivedAmount - formData.totals.netAmount) >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {(receivedAmount - formData.totals.netAmount).toFixed(2)}
                  </div>
                </div>
              </div>

              <div style={styles.paymentOptions}>
                {[
                  { method: 'UPI', icon: '💳', label: 'UPI' },
                  { method: 'CASH', icon: '💵', label: 'CASH' },
                  { method: 'CREDIT', icon: '📝', label: 'CREDIT' }
                ].map((payment) => (
                  <button
                    key={payment.method}
                    style={{
                      ...styles.paymentButton,
                      ...(paymentMethod === payment.method ? styles.paymentButtonActive : styles.paymentButtonInactive)
                    }}
                    onClick={() => handlePaymentMethod(payment.method)}
                  >
                    {payment.icon} {payment.label}
                  </button>
                ))}
              </div>

              {paymentMethod === 'UPI' && (
                <div style={styles.upiSection}>
                  <div style={styles.upiInputGroup}>
                    <label style={styles.upiLabel}>UPI ID Name</label>
                    <input
                      type="text"
                      name="name"
                      value={upiData.name}
                      onChange={handleUpiInputChange}
                      style={styles.upiInput}
                      placeholder="First Name Last Name"
                    />
                  </div>
                  <div style={styles.upiInputGroup}>
                    <label style={styles.upiLabel}>UPI ID No</label>
                    <input
                      type="text"
                      name="id"
                      value={upiData.id}
                      onChange={handleUpiInputChange}
                      style={styles.upiInput}
                      placeholder="9876543210@paytm"
                    />
                  </div>
                  <div style={styles.upiInputGroup}>
                    <label style={styles.upiLabel}>UPI ID Amount</label>
                    <input
                      type="text"
                      name="amount"
                      value={upiData.amount}
                      onChange={handleUpiInputChange}
                      style={styles.upiInput}
                      placeholder="UPI Amt"
                    />
                  </div>
                </div>
              )}

              <div style={styles.keypadSection}>
                <div style={styles.keypad}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <button
                      key={num}
                      style={styles.keyButton}
                      onClick={() => handleKeypadPress(num.toString())}
                      onMouseEnter={(e) => (e.target.style.backgroundColor = '#f3f4f6')}
                      onMouseLeave={(e) => (e.target.style.backgroundColor = '#ffffff')}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    style={styles.keyButton}
                    onClick={() => handleKeypadPress('.')}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#ffffff')}
                  >
                    .
                  </button>
                  <button
                    style={styles.keyButton}
                    onClick={() => handleKeypadPress('0')}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#ffffff')}
                  >
                    0
                  </button>
                  <button
                    style={{
                      ...styles.keyButton,
                      backgroundColor: '#ef4444',
                      color: '#ffffff',
                      border: '2px solid #dc2626'
                    }}
                    onClick={() => handleKeypadPress('←')}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#dc2626')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#ef4444')}
                  >
                    ←
                  </button>
                </div>
                <button
                  style={styles.clearButton}
                  onClick={handleClear}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#dc2626';
                    e.target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.color = '#dc2626';
                  }}
                >
                  CLEAR
                </button>
              </div>

              <button
                style={styles.takeBillButton}
                onClick={handleTakeBill}
                onMouseEnter={(e) => (e.target.style.backgroundColor = '#059669')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = '#10b981')}
              >
                <span style={{
                  backgroundColor: '#047857',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}>
                  F4
                </span>
                TAKE BILL
              </button>
            </div>
          </>
        )}

        {showRemarksForm && (
          <>
            <div style={styles.overlay} onClick={handleRemarksCancel} />
            <div style={styles.remarksModal}>
              <div style={styles.remarksHeader}>
                <span>REMARKS</span>
                <button
                  style={styles.closeButtonRed}
                  onClick={handleRemarksCancel}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = '#dc2626')}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = '#ef4444')}
                >
                  ×
                </button>
              </div>
              <div style={styles.remarksContent}>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="remarksOption"
                      checked={!useCommonTerms}
                      onChange={() => setUseCommonTerms(false)}
                      style={styles.radioInput}
                    />
                    Use This
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="remarksOption"
                      checked={useCommonTerms}
                      onChange={() => setUseCommonTerms(true)}
                      style={styles.radioInput}
                    />
                    Use This & Common Terms Also
                  </label>
                </div>
                <div style={styles.textareaGroup}>
                  <label style={styles.textareaLabel}>Remarks</label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    style={styles.textarea}
                    placeholder="Enter remarks..."
                  />
                </div>
                <div style={styles.textareaGroup}>
                  <label style={styles.textareaLabel}>Other Details</label>
                  <textarea
                    value={formData.otherDetails}
                    onChange={(e) => setFormData(prev => ({ ...prev, otherDetails: e.target.value }))}
                    style={styles.textarea}
                    placeholder="Enter other details..."
                  />
                </div>
                <div style={styles.remarksButtons}>
                  <button
                    style={{ ...styles.actionButton, ...styles.saveButton }}
                    onClick={handleRemarksSave}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>+</span>
                    SAVE
                  </button>
                  <button
                    style={{ ...styles.actionButton, ...styles.cancelButton }}
                    onClick={handleRemarksCancel}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>×</span>
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {showCustomerForm && (
          <CustomerForm
            onClose={() => setShowCustomerForm(false)}
            onSuccess={(customer) => {
              setFormData(prev => ({ ...prev, customer: customer.customerName, phone: customer.mobileNo }));
              setShowCustomerForm(false);
              setMessage('Customer added successfully!');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SalesForm;